import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  generateCouponCodes,
  bulkCreateCouponCodes,
  fetchCoupons,
  toggleCouponStatus,
  deleteCoupon,
  createCoupon,
  updateCoupon,
  fetchCouponAnalytics,
  fetchMostUsedCoupons,
  downloadCouponReport,
  fetchCouponDashboardStats,
  fetchExpiringCoupons,
  fetchRecentCouponUsage,
  // New client-facing services
  fetchExpiringCouponsForClient,
  fetchAvailableCouponsForCustomBasedOnOrderAmount,
  fetchCouponForFeaturedDeals,
  fetchCouponForFirstTimeCustomer,
  fetchClientCouponUsageHistory,
  fetchClientCouponForSpecificCategory
} from '../../services/couponService';

// Existing admin thunks...
export const thunkGenerateCouponCodes = createAsyncThunk(
  'coupon/generateCodes',
  async (payload, { rejectWithValue }) => {
    try {
      return await generateCouponCodes(payload);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const thunkBulkCreateCouponCodes = createAsyncThunk(
  'coupon/bulkCreate',
  async (payload, { rejectWithValue }) => {
    try {
      return await bulkCreateCouponCodes(payload);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const thunkFetchCoupons = createAsyncThunk(
  'coupon/fetchCoupons',
  async (params, { rejectWithValue }) => {
    try {
      return await fetchCoupons(params);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const thunkToggleCouponStatus = createAsyncThunk(
  'coupon/toggleStatus',
  async (couponId, { rejectWithValue }) => {
    try {
      return await toggleCouponStatus(couponId);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const thunkDeleteCoupon = createAsyncThunk(
  'coupon/deleteCoupon',
  async (couponId, { rejectWithValue }) => {
    try {
      await deleteCoupon(couponId);
      return couponId;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const thunkCreateCoupon = createAsyncThunk(
  'coupon/createCoupon',
  async (couponData, { rejectWithValue }) => {
    try {
      return await createCoupon(couponData);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const thunkUpdateCoupon = createAsyncThunk(
  'coupon/updateCoupon',
  async ({ couponId, couponData }, { rejectWithValue }) => {
    try {
      return await updateCoupon(couponId, couponData);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const thunkFetchCouponAnalytics = createAsyncThunk(
  'coupon/fetchAnalytics',
  async (period, { rejectWithValue }) => {
    try {
      return await fetchCouponAnalytics(period);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const thunkFetchMostUsedCoupons = createAsyncThunk(
  'coupon/fetchMostUsedCoupons',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchMostUsedCoupons();
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const thunkDownloadCouponReport = createAsyncThunk(
  'coupon/downloadReport',
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      return await downloadCouponReport(startDate, endDate);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const thunkFetchCouponDashboardStats = createAsyncThunk(
  'coupon/fetchDashboardStats',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchCouponDashboardStats();
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const thunkFetchExpiringCoupons = createAsyncThunk(
  'coupon/fetchExpiringCoupons',
  async (days = 7, { rejectWithValue }) => {
    try {
      return await fetchExpiringCoupons(days);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const thunkFetchRecentCouponUsage = createAsyncThunk(
  'coupon/fetchRecentCouponUsage',
  async (page_size = 5, { rejectWithValue }) => {
    try {
      return await fetchRecentCouponUsage(page_size);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// NEW CLIENT-FACING THUNKS
export const thunkFetchExpiringCouponsForClient = createAsyncThunk(
  'coupon/fetchExpiringCouponsForClient',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchExpiringCouponsForClient();
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const thunkFetchAvailableCouponsForCustomBasedOnOrderAmount = createAsyncThunk(
  'coupon/fetchAvailableCouponsForCustomBasedOnOrderAmount',
  async (amount, { rejectWithValue }) => {
    try {
      return await fetchAvailableCouponsForCustomBasedOnOrderAmount(amount);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const thunkFetchCouponForFeaturedDeals = createAsyncThunk(
  'coupon/fetchCouponForFeaturedDeals',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchCouponForFeaturedDeals();
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const thunkFetchCouponForFirstTimeCustomer = createAsyncThunk(
  'coupon/fetchCouponForFirstTimeCustomer',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchCouponForFirstTimeCustomer();
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const thunkFetchClientCouponUsageHistory = createAsyncThunk(
  'coupon/fetchClientCouponUsageHistory',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchClientCouponUsageHistory();
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const thunkFetchClientCouponForCategory = createAsyncThunk(
  'coupon/fetchClientCouponForCategory',
  async (categoryId, { rejectWithValue }) => {
    try {
      return await fetchClientCouponForSpecificCategory(categoryId);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

const couponSlice = createSlice({
  name: 'coupon',
  initialState: {
    // Existing admin state
    loading: false,
    error: null,
    codes: [],
    createdCount: 0,
    message: '',
    coupons: [],
    count: 0,
    page: 1,
    totalPages: 1,
    analytics: null,
    mostUsedCoupons: [],
    reportBlob: null,
    dashboardStats: null,
    expiringCoupons: [],
    recentActivity: [],

    // NEW CLIENT-FACING STATE
    clientLoading: false,
    clientError: null,

    // Client coupon data
    clientExpiringCoupons: [],
    availableCouponsForOrder: [],
    orderAmount: 0,
    featuredDeals: [],
    firstTimeOffers: [],
    clientUsageHistory: [],

    // Client metadata
    isFirstTimeCustomer: false,
    totalClientSavings: 0,
    bestAvailableDiscount: 0,

    //category coupons
    clientCategoryCoupons: []

  },
  reducers: {
    resetCouponState: (state) => {
      // Reset admin state
      state.loading = false;
      state.error = null;
      state.codes = [];
      state.createdCount = 0;
      state.message = '';
      state.coupons = [];
      state.count = 0;
      state.page = 1;
      state.totalPages = 1;
      state.analytics = null;
      state.mostUsedCoupons = [];
      state.reportBlob = null;
      state.dashboardStats = null;
      state.expiringCoupons = [];
      state.recentActivity = [];

    },

    // NEW CLIENT REDUCERS
    resetClientCouponState: (state) => {
      state.clientLoading = false;
      state.clientError = null;
      state.clientExpiringCoupons = [];
      state.availableCouponsForOrder = [];
      state.orderAmount = 0;
      state.featuredDeals = [];
      state.firstTimeOffers = [];
      state.clientUsageHistory = [];
      state.isFirstTimeCustomer = false;
      state.totalClientSavings = 0;
      state.bestAvailableDiscount = 0;
    },

    setOrderAmount: (state, action) => {
      state.orderAmount = action.payload;
    },

    clearAvailableCoupons: (state) => {
      state.availableCouponsForOrder = [];
      state.bestAvailableDiscount = 0;
    }
  },
  extraReducers: (builder) => {
    builder
      // Existing admin cases...
      .addCase(thunkGenerateCouponCodes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunkGenerateCouponCodes.fulfilled, (state, action) => {
        state.loading = false;
        state.codes = action.payload.codes || [];
        state.error = null;
      })
      .addCase(thunkGenerateCouponCodes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to generate codes';
      })

      .addCase(thunkBulkCreateCouponCodes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunkBulkCreateCouponCodes.fulfilled, (state, action) => {
        state.loading = false;
        state.createdCount = action.payload.created_count || 0;
        state.codes = action.payload.codes || [];
        state.message = `Successfully created ${action.payload.created_count} coupons!`;
        state.error = null;
      })
      .addCase(thunkBulkCreateCouponCodes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create coupons';
      })

      .addCase(thunkFetchCoupons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunkFetchCoupons.fulfilled, (state, action) => {
        state.loading = false;
        state.coupons = action.payload || [];
        state.count = action.payload.count || 0;
        state.totalPages = Math.ceil((action.payload.count || 1) / 20);
      })
      .addCase(thunkFetchCoupons.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch coupons';
      })

      .addCase(thunkToggleCouponStatus.fulfilled, (state, action) => {
        // Optionally update coupon status in state.coupons
      })

      .addCase(thunkDeleteCoupon.fulfilled, (state, action) => {
        state.coupons = state.coupons.filter(c => c.id !== action.payload);
      })

      .addCase(thunkCreateCoupon.fulfilled, (state, action) => {
        state.coupons.unshift(action.payload);
      })

      .addCase(thunkUpdateCoupon.fulfilled, (state, action) => {
        const idx = state.coupons.findIndex(c => c.id === action.payload.id);
        if (idx !== -1) state.coupons[idx] = action.payload;
      })

      .addCase(thunkFetchCouponAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunkFetchCouponAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.analytics = action.payload;
      })
      .addCase(thunkFetchCouponAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(thunkFetchMostUsedCoupons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunkFetchMostUsedCoupons.fulfilled, (state, action) => {
        state.loading = false;
        state.mostUsedCoupons = action.payload?.results || action.payload || [];
      })
      .addCase(thunkFetchMostUsedCoupons.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(thunkDownloadCouponReport.fulfilled, (state, action) => {
        state.reportBlob = action.payload;
      })

      .addCase(thunkFetchCouponDashboardStats.fulfilled, (state, action) => {
        state.dashboardStats = action.payload;
      })

      .addCase(thunkFetchExpiringCoupons.fulfilled, (state, action) => {
        state.expiringCoupons = action.payload;
      })

      .addCase(thunkFetchRecentCouponUsage.fulfilled, (state, action) => {
        state.recentActivity = action.payload.results || [];
      })

      // NEW CLIENT-FACING CASES

      // Client expiring coupons
      .addCase(thunkFetchExpiringCouponsForClient.pending, (state) => {
        state.clientLoading = true;
        state.clientError = null;
      })
      .addCase(thunkFetchExpiringCouponsForClient.fulfilled, (state, action) => {
        state.clientLoading = false;
        state.clientExpiringCoupons = action.payload.expiring_coupons || [];
        state.clientError = null;
      })
      .addCase(thunkFetchExpiringCouponsForClient.rejected, (state, action) => {
        state.clientLoading = false;
        state.clientError = action.payload || 'Failed to fetch expiring coupons';
      })

      // Available coupons for order amount
      .addCase(thunkFetchAvailableCouponsForCustomBasedOnOrderAmount.pending, (state) => {
        state.clientLoading = true;
        state.clientError = null;
      })
      .addCase(thunkFetchAvailableCouponsForCustomBasedOnOrderAmount.fulfilled, (state, action) => {
        state.clientLoading = false;
        state.availableCouponsForOrder = action.payload.available_coupons || [];
        state.orderAmount = action.payload.order_amount || 0;
        state.bestAvailableDiscount = action.payload.best_discount || 0;
        state.clientError = null;
      })
      .addCase(thunkFetchAvailableCouponsForCustomBasedOnOrderAmount.rejected, (state, action) => {
        state.clientLoading = false;
        state.clientError = action.payload || 'Failed to fetch available coupons';
      })

      // Featured deals
      .addCase(thunkFetchCouponForFeaturedDeals.pending, (state) => {
        state.clientLoading = true;
        state.clientError = null;
      })
      .addCase(thunkFetchCouponForFeaturedDeals.fulfilled, (state, action) => {
        state.clientLoading = false;
        state.featuredDeals = action.payload.featured_deals || [];
        state.clientError = null;
      })
      .addCase(thunkFetchCouponForFeaturedDeals.rejected, (state, action) => {
        state.clientLoading = false;
        state.clientError = action.payload || 'Failed to fetch featured deals';
      })

      // First time customer offers
      .addCase(thunkFetchCouponForFirstTimeCustomer.pending, (state) => {
        state.clientLoading = true;
        state.clientError = null;
      })
      .addCase(thunkFetchCouponForFirstTimeCustomer.fulfilled, (state, action) => {
        state.clientLoading = false;
        state.firstTimeOffers = action.payload.first_time_offers || [];
        state.isFirstTimeCustomer = action.payload.eligible || false;
        state.clientError = null;
      })
      .addCase(thunkFetchCouponForFirstTimeCustomer.rejected, (state, action) => {
        state.clientLoading = false;
        state.clientError = action.payload || 'Failed to fetch first time offers';
      })

      // Client usage history
      .addCase(thunkFetchClientCouponUsageHistory.pending, (state) => {
        state.clientLoading = true;
        state.clientError = null;
      })
      .addCase(thunkFetchClientCouponUsageHistory.fulfilled, (state, action) => {
        state.clientLoading = false;
        state.clientUsageHistory = action.payload.usage_history || [];
        state.totalClientSavings = action.payload.summary?.total_savings || 0;
        state.clientError = null;
      })
      .addCase(thunkFetchClientCouponUsageHistory.rejected, (state, action) => {
        state.clientLoading = false;
        state.clientError = action.payload || 'Failed to fetch usage history';
      })
      // Client category coupons
      .addCase(thunkFetchClientCouponForCategory.pending, (state) => {
        state.clientLoading = true;
        state.clientError = null;
      })
      .addCase(thunkFetchClientCouponForCategory.fulfilled, (state, action) => {
        state.clientLoading = false;
        state.clientCategoryCoupons = action.payload.coupons || [];
        state.clientError = null;
      })
      .addCase(thunkFetchClientCouponForCategory.rejected, (state, action) => {
        state.clientLoading = false;
        state.clientError = action.payload || 'Failed to fetch category coupons';
        state.clientCategoryCoupons = [];
      })


  }
});

export const {
  resetCouponState,
  resetClientCouponState,
  setOrderAmount,
  clearAvailableCoupons
} = couponSlice.actions;

export default couponSlice.reducer;